if (document.body?.classList.contains('sc-framework-reveal-enabled')) {
  document.documentElement.classList.add('sc-reveal-ready');
}

class ScDisclosureDrawer extends HTMLElement {
  connectedCallback() {
    this.openButtons = this.querySelectorAll('[data-sc-drawer-open]');
    this.closeButtons = this.querySelectorAll('[data-sc-drawer-close]');
    this.panel = this.querySelector('[data-sc-drawer-panel]');
    this.openTrigger = null;
    this.autoOpenEnabled = this.dataset.autoOpen === 'true';
    this.autoOpenDelay = Number(this.dataset.autoOpenDelay || 60);
    this.autoOpenScroll = Number(this.dataset.autoOpenScroll || 75);
    this.frequencyDays = Number(this.dataset.frequencyDays || 7);
    this.storageKey = this.dataset.storageKey || `sc_relationship_drawer_${this.id || 'default'}`;
    this.autoOpenTimer = null;
    this.autoOpenScrollHandler = null;
    this.handleHostClick = (event) => {
      if (event.target === this) this.close();
    };
    this.handleKeydown = (event) => this.onKeydown(event);
    this.openHandlers = Array.from(this.openButtons, (button) => {
      const handler = () => this.open(button);
      button.addEventListener('click', handler);
      return { button, handler };
    });
    this.closeHandlers = Array.from(this.closeButtons, (button) => {
      const handler = () => this.close();
      button.addEventListener('click', handler);
      return { button, handler };
    });
    this.addEventListener('click', this.handleHostClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.setOpenState(false);
    this.scheduleAutoOpen();
  }

  disconnectedCallback() {
    this.openHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.closeHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.removeEventListener('click', this.handleHostClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.clearAutoOpen();
  }

  open(trigger) {
    this.clearAutoOpen();
    this.openTrigger = trigger || document.activeElement;
    this.setAttribute('open', '');
    this.setOpenState(true);
    const firstControl = this.panel?.querySelector('[data-sc-drawer-close], a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    (firstControl || this.panel)?.focus();
  }

  close() {
    const wasOpen = this.hasAttribute('open');
    this.removeAttribute('open');
    this.setOpenState(false);
    if (wasOpen) this.rememberDismissal();
    if (this.openTrigger instanceof HTMLElement) {
      this.openTrigger.focus();
    }
  }

  readStorage() {
    try {
      return window.localStorage?.getItem(this.storageKey) || null;
    } catch (error) {
      return null;
    }
  }

  rememberDismissal() {
    try {
      window.localStorage?.setItem(this.storageKey, String(Date.now()));
    } catch (error) {
      // The drawer still works when storage is restricted; only frequency capping is skipped.
    }
  }

  wasRecentlyDismissed() {
    const stored = Number(this.readStorage());
    if (!stored || !Number.isFinite(stored)) return false;
    return Date.now() - stored < this.frequencyDays * 24 * 60 * 60 * 1000;
  }

  scheduleAutoOpen() {
    if (!this.autoOpenEnabled || this.wasRecentlyDismissed()) return;

    this.autoOpenTimer = window.setTimeout(() => {
      if (!this.hasAttribute('open') && !this.wasRecentlyDismissed()) this.open();
    }, Math.max(0, this.autoOpenDelay) * 1000);

    this.autoOpenScrollHandler = () => {
      if (this.hasAttribute('open') || this.wasRecentlyDismissed()) return;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = scrollableHeight > 0 ? (window.scrollY / scrollableHeight) * 100 : 0;
      if (scrollProgress >= this.autoOpenScroll) this.open();
    };
    window.addEventListener('scroll', this.autoOpenScrollHandler, { passive: true });
  }

  clearAutoOpen() {
    if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);
    if (this.autoOpenScrollHandler) window.removeEventListener('scroll', this.autoOpenScrollHandler);
    this.autoOpenTimer = null;
    this.autoOpenScrollHandler = null;
  }

  setOpenState(isOpen) {
    this.openButtons?.forEach((button) => button.setAttribute('aria-expanded', String(isOpen)));
    if (this.panel) {
      this.panel.setAttribute('aria-hidden', String(!isOpen));
      this.panel.inert = !isOpen;
    }
    document.documentElement.classList.toggle('sc-modal-open', isOpen);
  }

  onKeydown(event) {
    if (!this.hasAttribute('open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;

    const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusable.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('sc-disclosure-drawer')) {
  customElements.define('sc-disclosure-drawer', ScDisclosureDrawer);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-sc-open-relationship-drawer]');
  if (!trigger) return;

  const drawer = document.querySelector('sc-disclosure-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  event.preventDefault();
  const headerDrawer = trigger.closest('header-drawer');
  if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
    headerDrawer.closeMenuDrawer(event, trigger);
  }
  drawer.open(trigger);
});

class ScAgeGate extends HTMLElement {
  connectedCallback() {
    this.storageKey = this.dataset.storageKey || 'sc_age_verified';
    this.verificationMethod = this.dataset.verificationMethod || 'confirmation';
    this.minimumAge = Number(this.dataset.minimumAge || 18);
    this.rememberDays = Number(this.dataset.rememberDays || 30);
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-sc-age-error]');
    this.denyButtons = Array.from(this.querySelectorAll('[data-sc-age-deny]'));
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDeny = this.handleDeny.bind(this);

    if (this.isVerified()) {
      this.remove();
      return;
    }

    document.documentElement.classList.add('sc-age-gate-active');
    document.documentElement.classList.add('sc-modal-open');
    this.form?.addEventListener('submit', this.handleSubmit);
    this.denyButtons.forEach((button) => button.addEventListener('click', this.handleDeny));
    window.requestAnimationFrame(() => {
      this.querySelector('input, button, [tabindex]:not([tabindex="-1"])')?.focus();
    });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.denyButtons?.forEach((button) => button.removeEventListener('click', this.handleDeny));
    document.documentElement.classList.remove('sc-modal-open');
  }

  isVerified() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      return stored && stored.expiresAt && Date.now() < stored.expiresAt;
    } catch (_error) {
      return false;
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    if (this.verificationMethod === 'date_of_birth') {
      const formData = new FormData(this.form);
      const dateOfBirth = formData.get('date_of_birth');
      if (!dateOfBirth || !this.isOldEnough(dateOfBirth)) {
        this.showError();
        return;
      }
    }

    this.verify();
  }

  handleDeny(event) {
    event.preventDefault();
    this.showError();
  }

  verify() {
    const expiresAt = Date.now() + this.rememberDays * 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ verified: true, expiresAt }));
    } catch (_error) {
      // Verification should still unlock the page when storage is unavailable.
    }
    document.documentElement.classList.remove('sc-age-gate-active');
    document.documentElement.classList.remove('sc-modal-open');
    this.remove();
  }

  showError() {
    this.error?.removeAttribute('hidden');
    this.error?.focus?.();
  }

  isOldEnough(value) {
    const dob = new Date(value);
    if (Number.isNaN(dob.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age >= this.minimumAge;
  }
}

if (!customElements.get('sc-age-gate')) {
  customElements.define('sc-age-gate', ScAgeGate);
}

class ScContentHub extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-sc-hub-filter]'));
    this.items = Array.from(this.querySelectorAll('[data-sc-hub-item]'));
    this.filterHandlers = this.buttons.map((button) => {
      const handler = () => this.filter(button.dataset.scHubFilter || 'all');
      button.addEventListener('click', handler);
      return { button, handler };
    });
  }

  disconnectedCallback() {
    this.filterHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
  }

  filter(value) {
    this.buttons.forEach((button) => {
      const isActive = (button.dataset.scHubFilter || 'all') === value;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.items.forEach((item) => {
      const shouldShow = value === 'all' || item.dataset.scHubCategory === value;
      item.toggleAttribute('hidden', !shouldShow);
    });
  }
}

if (!customElements.get('sc-content-hub')) {
  customElements.define('sc-content-hub', ScContentHub);
}

class ScAnchorNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('[data-sc-anchor-link]'));
    this.targets = this.links
      .map((link) => document.getElementById(link.dataset.scAnchorLink))
      .filter(Boolean);

    if (!this.links.length || !this.targets.length || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => this.updateActive(entries), {
      rootMargin: '-35% 0px -55% 0px',
      threshold: 0,
    });

    this.targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  updateActive(entries) {
    const visible = entries.find((entry) => entry.isIntersecting);
    if (!visible) return;
    const activeId = visible.target.id;
    this.links.forEach((link) => {
      const isActive = link.dataset.scAnchorLink === activeId;
      link.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('sc-anchor-nav')) {
  customElements.define('sc-anchor-nav', ScAnchorNav);
}

class ScVideoHero extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-sc-video-toggle]');
    this.videos = Array.from(this.querySelectorAll('video'));
    this.handleToggle = () => this.toggleVideos();
    this.handleVisibility = () => this.syncPlayback();
    this.handleViewport = () => this.syncPlayback();
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.toggle?.addEventListener('click', this.handleToggle);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.motionQuery.addEventListener?.('change', this.handleVisibility);
    this.mobileQuery.addEventListener?.('change', this.handleViewport);
    this.syncPlayback();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.handleToggle);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.motionQuery?.removeEventListener?.('change', this.handleVisibility);
    this.mobileQuery?.removeEventListener?.('change', this.handleViewport);
  }

  toggleVideos() {
    if (!this.videos.length) return;
    const activeVideo = this.getActiveVideo();
    if (!activeVideo) return;
    const shouldPlay = activeVideo.paused;
    this.videos.forEach((video) => {
      if (video === activeVideo && shouldPlay) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    this.updateToggle(shouldPlay);
  }

  getActiveVideo() {
    const mobileVideo = this.querySelector('.sc-video-hero__video--mobile');
    const desktopVideo = this.querySelector('.sc-video-hero__video--desktop');
    if (this.mobileQuery?.matches && mobileVideo) return mobileVideo;
    return desktopVideo || mobileVideo || null;
  }

  syncPlayback() {
    const activeVideo = this.getActiveVideo();
    const shouldPlay = this.dataset.autoplay === 'true' && !this.motionQuery?.matches && !document.hidden;
    this.videos.forEach((video) => {
      if (video === activeVideo && shouldPlay) video.play().catch(() => {});
      else video.pause();
    });
    this.updateToggle(Boolean(activeVideo && !activeVideo.paused));
  }

  updateToggle(isPlaying) {
    if (!this.toggle) return;
    this.toggle.textContent = isPlaying ? 'Pause' : 'Play';
    this.toggle.setAttribute('aria-label', isPlaying ? 'Pause background video' : 'Play background video');
  }
}

if (!customElements.get('sc-video-hero')) {
  customElements.define('sc-video-hero', ScVideoHero);
}

class ScGiftMessage extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('[data-sc-gift-toggle]');
    this.field = this.querySelector('[data-sc-gift-field]');
    this.count = this.querySelector('[data-sc-gift-count]');
    this.checkbox?.addEventListener('change', () => this.updateVisibility());
    this.field?.addEventListener('input', () => this.updateCount());
    this.updateVisibility();
    this.updateCount();
  }

  updateVisibility() {
    if (!this.checkbox || !this.field) return;
    const wrapper = this.querySelector('[data-sc-gift-wrapper]');
    wrapper?.toggleAttribute('hidden', !this.checkbox.checked);
    if (this.hasAttribute('data-sc-gift-product')) {
      this.field.disabled = !this.checkbox.checked;
    }
  }

  updateCount() {
    if (!this.field || !this.count) return;
    this.count.textContent = String(this.field.value.length);
  }
}

if (!customElements.get('sc-gift-message')) {
  customElements.define('sc-gift-message', ScGiftMessage);
}

if (!window.__scRevealInit) {
  window.__scRevealInit = true;
  const reveal = () => {
    const items = document.querySelectorAll('.sc-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach((item) => observer.observe(item));
  };
  document.addEventListener('DOMContentLoaded', reveal);
  document.addEventListener('shopify:section:load', reveal);
}

(() => {
  const sectionForLink = (link) => {
    if (!link?.hash || link.origin !== location.origin || link.pathname !== location.pathname) return null;
    if (link.search && link.search !== location.search) return null;
    try {
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      return target?.classList.contains('sc-section') ? target : null;
    } catch {
      return null;
    }
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const target = sectionForLink(link);
    if (!target) return;
    event.preventDefault();
    const drawer = link.closest('header-drawer');
    if (drawer) {
      drawer.closeMenuDrawer?.(event, drawer.querySelector('summary'));
      drawer.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }
    if (location.hash !== link.hash) history.pushState(null, '', link.hash);
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  let pending = false;
  const updateActive = () => {
    pending = false;
    const links = Array.from(document.querySelectorAll('.sc-header a[href*="#"]'));
    const entries = links.map((link) => ({ link, target: sectionForLink(link) })).filter(({ target }) => target);
    const targets = Array.from(new Set(entries.map(({ target }) => target)))
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
    const active = targets.filter((target) => target.getBoundingClientRect().top <= headerHeight + 80).pop();
    entries.forEach(({ link, target }) => {
      const selected = target === active;
      if (selected) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
      link.querySelector('span')?.classList.toggle('header__active-menu-item', selected);
      if (link.classList.contains('menu-drawer__menu-item')) link.classList.toggle('menu-drawer__menu-item--active', selected);
    });
  };
  const scheduleUpdate = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(updateActive);
  };
  document.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('pageshow', scheduleUpdate);
  document.addEventListener('shopify:section:load', scheduleUpdate);
  scheduleUpdate();
})();
